import { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { parseJwt } from 'helpers/decodeLocalStoreage';
import useLoader from 'hooks/useLoader';
import { getMasterEmployee } from 'data/repositories/auth/authentication.repository';
import {
    createTeamCalendarEvent,
    createTeamCalendarShareLink,
    deleteTeamCalendarEvent,
    deleteTeamCalendarShareLink,
    getTeamCalendarEvents,
    getTeamCalendarShareLinks,
    updateTeamCalendarEvent
} from 'data/repositories/TeamCalendar';
import {
    TeamCalendarEvent,
    TeamCalendarEventSearchRequest,
    TeamCalendarShareLink,
    TeamCalendarShareLinkCreateRequest
} from 'data/schemas/TeamCalendar';
import { AuthenticationResponse } from 'data/schemas/auth';
import { Extra } from 'models';
import { TeamCalendarEventDraft } from './components/EventModal';
import { formatDateLabel, getEventsForDate, getMonthRange } from './components/dateUtils';

const DEFAULT_EVENT_COLOR = '#2563eb';
const DEFAULT_CALENDAR_TEAMS = ['dev', 'system', 'ba'];
const DEFAULT_SHARE_EXPIRY_DAYS = 7;
const MAX_SHARE_EXPIRY_DAYS = 14;

function normalizeTeams(value?: string) {
    if (!value) {
        return [];
    }

    return value
        .split(',')
        .map(team => team.trim().toLowerCase())
        .filter(Boolean)
        .filter((team, index, teams) => teams.indexOf(team) === index);
}

function createDefaultDraft(currentUser?: Extra, selectedDate?: Date): TeamCalendarEventDraft {
    const baseDate = selectedDate ?? new Date();
    const start = new Date(baseDate);
    start.setHours(9, 0, 0, 0);
    const end = new Date(start);
    end.setHours(10, 0, 0, 0);

    return {
        title: '',
        description: '',
        startAt: start.toISOString(),
        endAt: end.toISOString(),
        isAllDay: false,
        teamName: '',
        ownerName: [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' '),
        ownerCitizenId: currentUser?.citizen_id ?? '',
        status: 'planned',
        eventType: 'general',
        color: DEFAULT_EVENT_COLOR,
        visibility: 'only_me',
        allowedViewerCitizenIds: [],
        allowedViewerTeams: [],
        createdBy: currentUser?.citizen_id ?? '',
        updatedBy: currentUser?.citizen_id ?? ''
    };
}

function createDefaultShareDraft(currentUser?: Extra, currentMonth?: Date): TeamCalendarShareLinkCreateRequest {
    const monthDate = currentMonth ?? new Date();
    const monthRange = getMonthRange(monthDate);

    return {
        scopeType: 'all',
        scopeValue: '',
        startDate: monthRange.startDate,
        endDate: monthRange.endDate,
        expiresAt: createShareExpiryIso(DEFAULT_SHARE_EXPIRY_DAYS),
        createdBy: currentUser?.citizen_id ?? ''
    };
}

function createShareExpiryIso(days: number) {
    const normalizedDays = Math.min(Math.max(days, 1), MAX_SHARE_EXPIRY_DAYS);
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + normalizedDays);
    expiryDate.setHours(23, 59, 59, 999);
    return expiryDate.toISOString();
}

function readCurrentUser() {
    const token = localStorage.getItem('MM_APPLICATION');
    if (!token) {
        return undefined;
    }

    const jwt = parseJwt(token);
    return JSON.parse(jwt.extra) as Extra;
}

function TeamCalendarManageViewModel() {
    const loading = useLoader();
    const currentUser = useMemo(() => readCurrentUser(), []);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [events, setEvents] = useState<TeamCalendarEvent[]>([]);
    const [shareLinks, setShareLinks] = useState<TeamCalendarShareLink[]>([]);
    const [employees, setEmployees] = useState<AuthenticationResponse[]>([]);
    const [filters, setFilters] = useState<TeamCalendarEventSearchRequest>({
        status: '',
        search: '',
        teamName: '',
        ownerCitizenId: '',
        visibility: ''
    });
    const [draft, setDraft] = useState<TeamCalendarEventDraft>(() => createDefaultDraft(currentUser));
    const [shareDraft, setShareDraft] = useState<TeamCalendarShareLinkCreateRequest>(() =>
        createDefaultShareDraft(currentUser)
    );
    const [shareExpiryDays, setShareExpiryDays] = useState(DEFAULT_SHARE_EXPIRY_DAYS);
    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isShareSaving, setIsShareSaving] = useState(false);
    const [isEmployeesLoading, setIsEmployeesLoading] = useState(false);
    const currentEmployee = useMemo(
        () => employees.find(employee => employee.ctizenID === currentUser?.citizen_id),
        [employees, currentUser?.citizen_id]
    );
    const currentUserTeams = useMemo(() => normalizeTeams(currentEmployee?.team), [currentEmployee?.team]);

    const loadEvents = async () => {
        try {
            setIsLoading(true);
            loading.show();
            const range = getMonthRange(currentMonth);
            const result = await getTeamCalendarEvents({
                ...range,
                ...filters,
                viewerCitizenId: currentUser?.citizen_id ?? '',
                viewerTeams: currentUserTeams.join(',')
            });
            setEvents(result ?? []);
        } catch (error) {
            Swal.fire('Calendar error', (error as Error).message, 'error');
        } finally {
            setIsLoading(false);
            loading.hide();
        }
    };

    const loadShareLinks = async () => {
        try {
            const result = await getTeamCalendarShareLinks();
            setShareLinks(result ?? []);
        } catch (error) {
            Swal.fire('Share link error', (error as Error).message, 'error');
        }
    };

    const loadEmployees = async () => {
        try {
            setIsEmployeesLoading(true);
            const result = await getMasterEmployee({ citizenId: '' });
            setEmployees(result ?? []);
        } catch (error) {
            Swal.fire('Employee error', (error as Error).message, 'error');
        } finally {
            setIsEmployeesLoading(false);
        }
    };

    useEffect(() => {
        loadEvents();
    }, [currentMonth, filters.status, filters.search, filters.teamName, filters.ownerCitizenId, filters.visibility, currentUserTeams.join(',')]);

    useEffect(() => {
        loadShareLinks();
    }, []);

    useEffect(() => {
        loadEmployees();
    }, []);

    const selectedDateEvents = useMemo(() => {
        return getEventsForDate(selectedDate, events);
    }, [selectedDate, events]);

    const upcomingEvents = useMemo(() => {
        return [...events]
            .sort((left, right) => new Date(left.startAt).getTime() - new Date(right.startAt).getTime())
            .slice(0, 8);
    }, [events]);

    const availableTeams = useMemo(() => {
        const eventTeams = events.map(event => event.teamName);
        const employeeTeams = employees.flatMap(employee => normalizeTeams(employee.team));
        return [...new Set([...DEFAULT_CALENDAR_TEAMS, ...eventTeams, ...employeeTeams])]
            .filter(Boolean)
            .sort();
    }, [employees, events]);

    const availableOwners = useMemo(() => {
        const ownerMap = new Map<string, string>();

        employees.forEach(employee => {
            if (employee.ctizenID) {
                ownerMap.set(employee.ctizenID, employee.nickName || employee.ctizenID);
            }
        });

        events.forEach(event => {
            if (event.ownerCitizenId && !ownerMap.has(event.ownerCitizenId)) {
                ownerMap.set(event.ownerCitizenId, event.ownerName || event.ownerCitizenId);
            }
        });

        return Array.from(ownerMap.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((left, right) => left.label.localeCompare(right.label));
    }, [employees, events]);

    const openCreateModal = (date?: Date) => {
        const nextDraft = createDefaultDraft(currentUser, date ?? selectedDate);
        nextDraft.teamName = currentUserTeams[0] ?? '';
        setDraft(nextDraft);
        setIsEventModalOpen(true);
    };

    const openEditModal = (event: TeamCalendarEvent) => {
        if (event.isSystemGenerated && event.sourceType === 'migration_runbook' && event.sourceId) {
            window.location.assign(`/migration-runbook/runs/${event.sourceId}`);
            return;
        }

        setDraft({
            id: event.id,
            title: event.title,
            description: event.description,
            startAt: event.startAt,
            endAt: event.endAt,
            isAllDay: event.isAllDay,
            teamName: event.teamName,
            ownerName: event.ownerName,
            ownerCitizenId: event.ownerCitizenId,
            status: event.status,
            eventType: event.eventType,
            color: event.color,
            visibility: event.visibility,
            allowedViewerCitizenIds: event.allowedViewerCitizenIds
                ? event.allowedViewerCitizenIds.split(',').map(value => value.trim()).filter(Boolean)
                : [],
            allowedViewerTeams: event.allowedViewerTeams
                ? normalizeTeams(event.allowedViewerTeams)
                : [],
            createdBy: event.createdBy,
            createdOn: event.createdOn,
            updatedBy: event.updatedBy,
            updatedOn: event.updatedOn
        });
        setIsEventModalOpen(true);
    };

    const saveEvent = async () => {
        try {
            if (!draft.title.trim()) {
                throw new Error('Title is required.');
            }

            if (new Date(draft.endAt).getTime() < new Date(draft.startAt).getTime()) {
                throw new Error('End time must be greater than or equal to start time.');
            }

            if (draft.visibility === 'selected_people' && draft.allowedViewerCitizenIds.length === 0) {
                throw new Error('Select at least one viewer.');
            }

            if (draft.visibility === 'selected_teams' && draft.allowedViewerTeams.length === 0) {
                throw new Error('Select at least one team.');
            }

            setIsSaving(true);
            loading.show();
            const payload = {
                ...draft,
                updatedBy: currentUser?.citizen_id ?? draft.updatedBy
            };
            if (draft.id) {
                await updateTeamCalendarEvent(draft.id, payload);
            } else {
                await createTeamCalendarEvent(payload);
            }
            setIsEventModalOpen(false);
            await loadEvents();
        } catch (error) {
            Swal.fire('Save failed', (error as Error).message, 'error');
        } finally {
            setIsSaving(false);
            loading.hide();
        }
    };

    const removeEvent = async () => {
        if (!draft.id) {
            return;
        }

        const result = await Swal.fire({
            title: 'Delete event?',
            text: 'This event will be hidden from the calendar.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete'
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            setIsSaving(true);
            loading.show();
            await deleteTeamCalendarEvent(draft.id, currentUser?.citizen_id ?? '');
            setIsEventModalOpen(false);
            await loadEvents();
        } catch (error) {
            Swal.fire('Delete failed', (error as Error).message, 'error');
        } finally {
            setIsSaving(false);
            loading.hide();
        }
    };

    const createShareLink = async () => {
        try {
            if (shareDraft.scopeType !== 'all' && !shareDraft.scopeValue.trim()) {
                throw new Error('Select a scope value before creating a share link.');
            }

            if (
                shareDraft.startDate &&
                shareDraft.endDate &&
                new Date(shareDraft.endDate).getTime() < new Date(shareDraft.startDate).getTime()
            ) {
                throw new Error('End date must be greater than or equal to start date.');
            }

            if (!shareDraft.expiresAt) {
                throw new Error('Select expiry within 1 to 14 days.');
            }

            setIsShareSaving(true);
            loading.show();
            await createTeamCalendarShareLink(shareDraft);
            setShareDraft(createDefaultShareDraft(currentUser, currentMonth));
            setShareExpiryDays(DEFAULT_SHARE_EXPIRY_DAYS);
            await loadShareLinks();
            Swal.fire('Created', 'Share link created successfully.', 'success');
        } catch (error) {
            Swal.fire('Share link failed', (error as Error).message, 'error');
        } finally {
            setIsShareSaving(false);
            loading.hide();
        }
    };

    const deleteShareLinkItem = async (shareLink: TeamCalendarShareLink) => {
        const result = await Swal.fire({
            title: 'Delete share link?',
            text: 'Anyone using this link will not be able to open the shared calendar anymore.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete'
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            loading.show();
            await deleteTeamCalendarShareLink(shareLink.id, currentUser?.citizen_id ?? '');
            await loadShareLinks();
            Swal.fire('Deleted', 'Share link deleted successfully.', 'success');
        } catch (error) {
            Swal.fire('Delete failed', (error as Error).message, 'error');
        } finally {
            loading.hide();
        }
    };

    const copyShareLink = async (shareLink: TeamCalendarShareLink) => {
        const url = `${window.location.origin}/calendar/view/${shareLink.shareToken}`;
        await navigator.clipboard.writeText(url);
        Swal.fire('Copied', 'Share link copied to clipboard.', 'success');
    };

    const selectedDateLabel = formatDateLabel(selectedDate);
    const canEditDraft =
        !draft.id ||
        draft.createdBy === (currentUser?.citizen_id ?? '') ||
        draft.ownerCitizenId === (currentUser?.citizen_id ?? '');

    const updateDraft = <K extends keyof TeamCalendarEventDraft>(
        key: K,
        value: TeamCalendarEventDraft[K]
    ) => {
        setDraft(prevState => {
            const nextState = { ...prevState, [key]: value };

            if (key === 'visibility' && value !== 'selected_people') {
                nextState.allowedViewerCitizenIds = [];
            }

            if (key === 'visibility' && value !== 'selected_teams') {
                nextState.allowedViewerTeams = [];
            }

            return nextState;
        });
    };

    const updateShareDraft = <K extends keyof TeamCalendarShareLinkCreateRequest>(
        key: K,
        value: TeamCalendarShareLinkCreateRequest[K]
    ) => {
        setShareDraft(prevState => {
            const nextState = { ...prevState, [key]: value };

            if (key === 'scopeType') {
                nextState.scopeValue = '';
            }

            return nextState;
        });
    };

    const updateShareExpiryDays = (days: number) => {
        const normalizedDays = Math.min(Math.max(days, 1), MAX_SHARE_EXPIRY_DAYS);
        setShareExpiryDays(normalizedDays);
        setShareDraft(prevState => ({
            ...prevState,
            expiresAt: createShareExpiryIso(normalizedDays)
        }));
    };

    return {
        currentMonth,
        selectedDate,
        selectedDateLabel,
        events,
        shareLinks,
        filters,
        draft,
        shareDraft,
        shareExpiryDays,
        isEventModalOpen,
        isShareModalOpen,
        isLoading,
        isSaving,
        isShareSaving,
        employees,
        isEmployeesLoading,
        selectedDateEvents,
        upcomingEvents,
        availableTeams,
        availableOwners,
        currentUserTeams,
        canEditDraft,

        setCurrentMonth,
        setSelectedDate,
        setIsEventModalOpen,
        setIsShareModalOpen,
        openCreateModal,
        openEditModal,
        saveEvent,
        removeEvent,
        createShareLink,
        deleteShareLinkItem,
        copyShareLink,
        reloadEvents: loadEvents,
        updateFilter: <K extends keyof TeamCalendarEventSearchRequest>(
            key: K,
            value: TeamCalendarEventSearchRequest[K]
        ) => setFilters(prevState => ({ ...prevState, [key]: value })),
        updateDraft,
        updateShareDraft,
        updateShareExpiryDays
    };
}

export default TeamCalendarManageViewModel;
